import React, {
  FC,
  ChangeEvent,
  useCallback,
  useEffect,
  useState,
  FocusEvent,
  useRef,
  KeyboardEvent,
} from 'react';
import {
  Box,
  SxProps,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material';
import { mergeSx, resolveMuiSlotProps } from '@client/helpers/mui.helpers';
import { useKeyboardEventCallback } from '@client/hooks/use-events.hooks';
import {
  useDebouncedFunction,
  useComponentUUID,
} from '@client/hooks/use-utils.hooks';
import { IconButton } from '@ui/buttons';
import { Icon } from './Icon';

const searchIconAdornmentSx = {
  px: 1.2,
  display: 'flex',
  color: 'rgba(0, 0, 0, 0.3)',
} as const;

type SearchFieldProps = Omit<TextFieldProps, 'onChange' | 'value'> & {
  value?: string;
  debounce?: number;
  autoFocus?: boolean;
  iconSize?: number;
  /** Applied filters displayed at the right end of the field. */
  filterTags?: React.ReactNode;
  trailingAction?: React.ReactNode;
  showClear?: boolean;
  onClear?: () => void;
  clearTooltip?: string;
  onChange: (str: string) => void;
};

const SearchField: FC<SearchFieldProps> = ({
  onChange,
  value = '',
  debounce = 0,
  autoFocus = false,
  iconSize,
  filterTags,
  trailingAction,
  showClear = false,
  onClear,
  clearTooltip = '',
  slotProps,
  sx,
  onFocus,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // A fresh name each mount, plus autoComplete="off", stops the browser from
  // matching values previously typed into this field.
  const generatedName = useComponentUUID();

  const [internalValue, setInternalValue] = useState(value);
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const debouncedOnChange = useDebouncedFunction((newValue: string) => {
    onChange(newValue);
  }, debounce);

  const isEmpty = value === '';

  const handleClear = useCallback(() => {
    setInternalValue('');
    if (onClear) onClear();
    else onChange('');
    inputRef.current?.focus();
  }, [onChange, onClear]);

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setInternalValue(e.target.value);
      debouncedOnChange(e.target.value);
    },
    [debouncedOnChange],
  );

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      e.target.select();
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleKeyDown = useKeyboardEventCallback<KeyboardEvent>(
    (e) => {
      if (e.key === 'Escape') handleClear();
    },
    { stopPropagation: !isEmpty },
  );

  return (
    <TextField
      {...props}
      autoComplete="off"
      name={props.name ?? generatedName}
      autoFocus={autoFocus}
      value={internalValue}
      onFocus={handleFocus}
      onChange={handleOnChange}
      onKeyDown={handleKeyDown}
      sx={mergeSx(overrideOutlineSx, sx)}
      slotProps={{
        ...slotProps,
        // Slot values may be ownerState callbacks; resolve before spreading.
        input: (ownerState) => ({
          startAdornment: (
            <>
              <Box
                onClick={() => inputRef.current?.focus()}
                sx={searchIconAdornmentSx}
              >
                <Icon category="mui" icon="Search" size={iconSize} />
              </Box>
            </>
          ),
          endAdornment: (
            <>
              {filterTags}
              <Tooltip title={clearTooltip} disableInteractive>
                <IconButton
                  category="mui"
                  icon="Clear"
                  size={12}
                  hidden={isEmpty && !showClear}
                  aria-label={clearTooltip || 'Clear search'}
                  onClick={handleClear}
                />
              </Tooltip>
              {trailingAction}
            </>
          ),
          ...resolveMuiSlotProps(slotProps?.input, ownerState),
        }),
        htmlInput: (ownerState) => ({
          ...resolveMuiSlotProps(slotProps?.htmlInput, ownerState),
          ref: inputRef,
        }),
      }}
    />
  );
};

const overrideOutlineSx: SxProps = {
  boxSizing: 'content-box',
  '& .MuiOutlinedInput-notchedOutline': {
    border: '0px none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: '0px none',
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: '0px none',
  },
};
export default SearchField;
